import React, { useEffect, useState } from 'react';
import {
  Table, Button, Modal, Form, Input, Switch, Popconfirm, message,
  Card, Space, Tooltip, Badge
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined,
  EyeOutlined, ReloadOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import dayjs from 'dayjs';

const Customers = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [detailRecord, setDetailRecord] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [resetPwdModalVisible, setResetPwdModalVisible] = useState(false);
  const [resetPwdLoading, setResetPwdLoading] = useState(false);
  
  const [form] = Form.useForm();
  const [resetPwdForm] = Form.useForm();
  const { getAuthHeaders, API_BASE_URL } = useAuth();

  useEffect(() => {
    fetchData();
  }, [pagination.current, pagination.pageSize, searchText]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/customers`, {
        headers: getAuthHeaders(),
        params: {
          page: pagination.current,
          per_page: pagination.pageSize,
          search: searchText
        }
      });
      if (response.data.success) {
        setData(response.data.data.items);
        setPagination(prev => ({ ...prev, total: response.data.data.total }));
      }
    } catch (error) {
      message.error('获取数据失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchDetail = async (id) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/customers/${id}`, {
        headers: getAuthHeaders()
      });
      if (response.data.success) {
        setDetailRecord(response.data.data);
        setDetailModalVisible(true);
      }
    } catch (error) {
      message.error('获取详情失败');
    }
  };

  const handleCreate = async (values) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/customers`, values, {
        headers: getAuthHeaders()
      });
      
      if (response.data.success) {
        message.success('客户创建成功');
        setModalVisible(false);
        form.resetFields();
        fetchData();
      }
    } catch (error) {
      message.error(error.response?.data?.message || '创建失败');
    }
  };

  const handleUpdate = async (values) => {
    try {
      const response = await axios.put(`${API_BASE_URL}/customers/${editingRecord.id}`, values, {
        headers: getAuthHeaders()
      });
      
      if (response.data.success) {
        message.success('客户信息更新成功');
        setModalVisible(false);
        setEditingRecord(null);
        form.resetFields();
        fetchData();
      }
    } catch (error) {
      message.error(error.response?.data?.message || '更新失败');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_BASE_URL}/customers/${id}`, {
        headers: getAuthHeaders()
      });
      message.success('删除成功');
      fetchData();
    } catch (error) {
      message.error(error.response?.data?.message || '删除失败');
    }
  };

  // 重置客户密码
  const handleResetPassword = async (values) => {
    if (!detailRecord) return;
    
    setResetPwdLoading(true);
    try {
      const response = await axios.put(
        `${API_BASE_URL}/customers/${detailRecord.id}`,
        { password: values.new_password },
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success) {
        message.success('密码重置成功');
        setResetPwdModalVisible(false);
        resetPwdForm.resetFields();
      }
    } catch (error) {
      message.error(error.response?.data?.message || '密码重置失败');
    } finally {
      setResetPwdLoading(false);
    }
  };

  const openEditModal = (record) => {
    setEditingRecord(record);
    form.setFieldsValue({
      username: record.username,
      company_name: record.company_name,
      contact_person: record.contact_person,
      email: record.email,
      phone: record.phone,
      notes: record.notes,
      is_active: record.is_active
    });
    setModalVisible(true);
  };

  const openCreateModal = () => {
    setEditingRecord(null);
    form.resetFields();
    setModalVisible(true);
  };

  const openDetailModal = (record) => {
    fetchDetail(record.id);
  };

  const columns = [
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username'
    },
    {
      title: '公司名称',
      dataIndex: 'company_name',
      key: 'company_name',
      render: (text) => text || '-'
    },
    {
      title: '联系人',
      dataIndex: 'contact_person',
      key: 'contact_person',
      render: (text) => text || '-'
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
      render: (text) => text || '-'
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Badge 
          status={isActive ? 'success' : 'error'} 
          text={isActive ? '有效' : '禁用'} 
        />
      )
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => text ? dayjs(text).format('YYYY-MM-DD HH:mm') : '-'
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Tooltip title="查看详情">
            <Button icon={<EyeOutlined />} size="small" onClick={() => openDetailModal(record)} />
          </Tooltip>
          <Tooltip title="编辑">
            <Button icon={<EditOutlined />} size="small" onClick={() => openEditModal(record)} />
          </Tooltip>
          <Popconfirm
            title="确定要删除吗？"
            description="删除前请确保该客户没有关联的序列号"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Card
        title="客户管理"
        extra={
          <Space>
            <Tooltip title="刷新">
              <Button icon={<ReloadOutlined />} onClick={fetchData} />
            </Tooltip>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
              新建客户
            </Button>
          </Space>
        }
      >
        <Space style={{ marginBottom: 16 }}>
          <Input.Search
            placeholder="搜索用户名、公司、联系人或邮箱"
            allowClear
            onSearch={setSearchText}
            style={{ width: 350 }}
            prefix={<SearchOutlined />}
          />
        </Space>
        
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`
          }}
          onChange={setPagination}
        />
      </Card>

      {/* 创建/编辑模态框 */}
      <Modal
        title={editingRecord ? '编辑客户' : '新建客户'}
        open={modalVisible}
        onCancel={() => { setModalVisible(false); setEditingRecord(null); form.resetFields(); }}
        onOk={() => form.submit()}
        destroyOnClose
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={editingRecord ? handleUpdate : handleCreate}
        >
          <Form.Item
            name="username"
            label="用户名"
            rules={[{ required: true, message: '请输入用户名' }]}
          >
            <Input placeholder="用于登录的用户名" disabled={!!editingRecord} />
          </Form.Item>
          
          {!editingRecord && (
            <Form.Item
              name="password"
              label="密码"
              rules={[{ required: true, message: '请输入密码' }]}
            >
              <Input.Password placeholder="设置登录密码" />
            </Form.Item>
          )}
          
          {editingRecord && (
            <div style={{ marginBottom: 16, padding: 12, background: '#f6ffed', borderRadius: 4 }}>
              <span style={{ color: '#52c41a' }}>💡 提示：修改密码请使用「查看详情」→「重置密码」功能</span>
            </div>
          )}
          
          <Form.Item
            name="company_name"
            label="公司名称"
          >
            <Input placeholder="可选" />
          </Form.Item>
          
          <Form.Item
            name="contact_person"
            label="联系人"
          >
            <Input placeholder="可选" />
          </Form.Item>
          
          <Form.Item
            name="email"
            label="邮箱"
            rules={[{ type: 'email', message: '请输入有效的邮箱地址' }]}
          >
            <Input placeholder="可选" />
          </Form.Item>
          
          <Form.Item
            name="phone"
            label="电话"
          >
            <Input placeholder="可选" />
          </Form.Item>
          
          {editingRecord && (
            <Form.Item
              name="is_active"
              label="账户状态"
              valuePropName="checked"
            >
              <Switch checkedChildren="有效" unCheckedChildren="禁用" />
            </Form.Item>
          )}
          
          <Form.Item
            name="notes"
            label="备注"
          >
            <Input.TextArea rows={3} placeholder="可选" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 详情模态框 */}
      <Modal
        title="客户详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="reset" type="primary" danger ghost onClick={() => setResetPwdModalVisible(true)}>
            重置密码
          </Button>,
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={700}
      >
        {detailRecord && (
          <div>
            <h3>基本信息</h3>
            <p><strong>用户名:</strong> {detailRecord.username}</p>
            <p><strong>公司名称:</strong> {detailRecord.company_name || '-'}</p>
            <p><strong>联系人:</strong> {detailRecord.contact_person || '-'}</p>
            <p><strong>邮箱:</strong> {detailRecord.email || '-'}</p>
            <p><strong>电话:</strong> {detailRecord.phone || '-'}</p>
            <p><strong>状态:</strong> {detailRecord.is_active ? '有效' : '禁用'}</p>
            <p><strong>备注:</strong> {detailRecord.notes || '-'}</p>
            
            <h3 style={{ marginTop: 24 }}>关联序列号</h3>
            {detailRecord.serials && detailRecord.serials.length > 0 ? (
              <Table
                dataSource={detailRecord.serials}
                rowKey="id"
                pagination={false}
                size="small"
                columns={[
                  { title: '序列号', dataIndex: 'serial_number', key: 'serial_number' },
                  { 
                    title: '激活次数', 
                    key: 'activations',
                    render: (_, r) => `${r.current_activations} / ${r.max_activations}`
                  },
                  { 
                    title: '状态', 
                    dataIndex: 'is_active',
                    render: (v) => v ? '有效' : '禁用'
                  }
                ]}
              />
            ) : (
              <p style={{ color: '#999' }}>暂无关联的序列号</p>
            )}
          </div>
        )}
      </Modal>

      {/* 重置密码模态框 */}
      <Modal
        title="重置客户密码"
        open={resetPwdModalVisible}
        onCancel={() => { setResetPwdModalVisible(false); resetPwdForm.resetFields(); }}
        onOk={() => resetPwdForm.submit()}
        confirmLoading={resetPwdLoading}
        destroyOnClose
      >
        <Form
          form={resetPwdForm}
          layout="vertical"
          onFinish={handleResetPassword}
        >
          <p style={{ marginBottom: 16 }}>
            正在为 <strong>{detailRecord?.username}</strong> 设置新密码
          </p>
          <Form.Item
            name="new_password"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '密码长度至少6位' }
            ]}
          >
            <Input.Password placeholder="输入新密码" />
          </Form.Item>
          <Form.Item
            name="confirm_password"
            label="确认新密码"
            rules={[
              { required: true, message: '请确认新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('new_password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="再次输入新密码" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Customers;
